/**
 * Compression kept with its original, as Unity and Godot do it. A pack entry
 * with `compress` loads a copy the editor made from an original kept outside
 * the folder the game serves:
 *
 *   { "type": "image", "key": "sky", "url": "sky.webp",
 *     "compress": { "source": "art/assets/sky.png", "format": "webp", "quality": 80, "maxSize": 1024 } }
 *
 * `source` is relative to the folder that holds project.json, like script
 * paths. The editor makes the copy again whenever a setting or the original
 * changes; `.animo/compressed.json` in that folder records what each copy was
 * made from.
 */
import { normalizeProjectPath } from './paths.js';

export type ImageCompressFormat = 'keep' | 'webp' | 'webp-lossless' | 'png';
export type AudioCompressFormat = 'keep' | 'ogg' | 'mp3' | 'both';

export interface ImageCompress {
  source: string;
  format: ImageCompressFormat;
  /** Lossy WebP quality, 1 to 100. */
  quality?: number;
  /** Longest side in pixels, for plain images; 0 or missing keeps the size. */
  maxSize?: number;
}

export interface AudioCompress {
  source: string;
  format: AudioCompressFormat;
  /** Bit rate in kbps. */
  kbps?: number;
  channels?: 1 | 2;
  /** Sample rate in Hz; missing keeps the original's. */
  rate?: number;
}

export type Compress = ImageCompress | AudioCompress;

/** Where the project keeps what each compressed copy was made from, relative to project.json's folder. */
export const COMPRESSED_MANIFEST = '.animo/compressed.json';

/** One pack entry that loads a compressed copy. */
export interface CompressedEntry {
  pack: string;
  section: string;
  index: number;
  key: string;
  type: string;
  compress: Compress;
  /** The copies it loads, as project paths: one per URL. */
  outputs: string[];
}

const IMAGE_FORMATS = ['keep', 'webp', 'webp-lossless', 'png'];
const AUDIO_FORMATS = ['keep', 'ogg', 'mp3', 'both'];

/** What is wrong with a `compress` value, or undefined when it is fine. */
export function compressProblem(type: string, value: unknown): string | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 'compress must be an object';
  const c = value as Record<string, unknown>;
  if (typeof c.source !== 'string' || !c.source) return 'compress needs the source file it is made from';
  try {
    if (normalizeProjectPath(c.source) !== c.source) return `compress source "${c.source}" is not a normal path`;
  } catch {
    return `compress source "${c.source}" is not a valid path`;
  }
  const formats = type === 'audio' ? AUDIO_FORMATS : IMAGE_FORMATS;
  if (!formats.includes(String(c.format))) return `compress format must be one of ${formats.join(', ')}`;
  return undefined;
}

/** Every pack entry with `compress`, and the copies it loads. */
export function compressedEntries(files: ReadonlyMap<string, unknown>): CompressedEntry[] {
  const out: CompressedEntry[] = [];
  for (const [pack, value] of files) {
    if (!pack.endsWith('.pack.json') || !value || typeof value !== 'object') continue;
    for (const [section, body] of Object.entries(value as Record<string, { path?: unknown; files?: unknown }>)) {
      if (section === 'meta' || !Array.isArray(body?.files)) continue;
      const base = typeof body.path === 'string' && body.path ? body.path.replace(/\/?$/, '/').replace(/^\/$/, '') : '';
      (body.files as Record<string, unknown>[]).forEach((e, index) => {
        if (!e || typeof e !== 'object' || typeof e.key !== 'string' || typeof e.type !== 'string') return;
        if (compressProblem(e.type, e.compress)) return;
        const urls = Array.isArray(e.url) ? e.url : [e.url ?? e.textureURL];
        const outputs: string[] = [];
        for (const u of urls) {
          if (typeof u !== 'string') continue;
          try {
            outputs.push(normalizeProjectPath(base + u));
          } catch {
            // Validation reports it.
          }
        }
        out.push({ pack, section, index, key: e.key, type: e.type, compress: e.compress as Compress, outputs });
      });
    }
  }
  return out;
}

/**
 * What a copy is made from: the original as it is now (`sourceState`, its
 * size and change time or a hash), the entry type and the settings. A copy
 * whose recorded stamp differs is made again.
 */
export function compressStamp(entry: Pick<CompressedEntry, 'type' | 'compress'>, sourceState: string | null | undefined): string {
  const { source: _source, ...settings } = entry.compress as Compress & Record<string, unknown>;
  const sorted = Object.fromEntries(Object.entries(settings).sort(([a], [b]) => a.localeCompare(b)));
  return `${sourceState ?? 'missing'}|${entry.type}|${JSON.stringify(sorted)}`;
}
